import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Euro, Shield, MessageCircle, Camera, Calendar, Download, Check, X, TrendingUp, Wallet, Users } from "lucide-react";

export default function BecomeHostPage() {
  const { user } = useAuth();
  const ctaTo = !user ? "/register?role=owner" : user.role === "owner" || user.role === "admin" ? "/owner" : "/register?role=owner";
  const ctaText = !user ? "Créer mon compte propriétaire" : user.role === "owner" ? "Accéder à mon espace" : "Devenir propriétaire";

  return (
    <div className="pt-24 sm:pt-28">
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1663925118130-16c1866b3d7c?w=2000" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-white">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.35em] mb-6 text-white/80">Propriétaires bretons</div>
            <h1 className="font-display font-black leading-[0.95] text-5xl sm:text-6xl tracking-tight">
              Reprenez le contrôle de
              <em className="italic font-light"> vos locations.</em>
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl leading-relaxed">
              Pas de commission opaque. Pas d'algorithme contre vous. Une plateforme bretonne,
              pensée par et pour les hôtes locaux.
            </p>
            <Link
              to={ctaTo}
              data-testid="become-host-cta"
              className="inline-flex items-center gap-3 mt-8 bg-white text-[#0A0A0A] px-8 py-4 hover:bg-[#C9986A] hover:text-white transition-colors font-semibold"
            >
              {ctaText} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">Pourquoi nous</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight max-w-3xl">
          Une alternative <em className="italic font-light">vraiment bretonne</em> à Airbnb.
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Download, t: "Import Airbnb en 1 clic", d: "Vous êtes déjà sur Airbnb ? Collez le lien : titre, photos, description, ville, tout est récupéré." },
            { icon: Euro, t: "Commission réduite", d: "10% seulement, calculés hors frais de ménage. Vous gardez l'essentiel." },
            { icon: Shield, t: "Paiement sécurisé Stripe", d: "Encaissez sereinement, vos voyageurs paient en confiance." },
            { icon: MessageCircle, t: "Messagerie directe", d: "Échangez sans intermédiaire avec vos voyageurs." },
            { icon: Camera, t: "Galeries soignées", d: "Mettez vos photos en valeur avec des galeries éditoriales." },
            { icon: Calendar, t: "Calendrier flexible", d: "Bloquez des dates ou variez les tarifs jour par jour." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="border border-black/10 p-8 hover:border-[#0A0A0A] transition-colors">
              <div className="w-12 h-12 border-2 border-[#0A0A0A] flex items-center justify-center mb-4">
                <Icon className="w-5 h-5" />
              </div>
              <div className="font-display text-xl font-bold">{t}</div>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 flex justify-center">
          <Link to={ctaTo} className="bg-[#0A0A0A] text-white px-10 py-4 font-semibold hover:bg-[#1B2A49] transition-colors">
            {ctaText}
          </Link>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="bg-[#F5F2EC] py-24 sm:py-32 bg-grain">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-600 font-bold mb-3 text-center">Comparatif honnête</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-center max-w-3xl mx-auto">
            Pourquoi choisir <em className="italic font-light">Mon Séjour en Bretagne</em>
          </h2>
          <p className="text-center text-neutral-600 mt-4 max-w-2xl mx-auto">
            Voici comment nous nous positionnons face aux géants — sans langue de bois.
          </p>

          {/* SIMULATOR */}
          <PlatformComparisonSimulator />

          {(() => {
            const ROWS = [
              { c: "Commission propriétaire", msb: "10% (hors ménage)", a: "3% hôte + 14% voyageur", b: "15-18% hôte" },
              { c: "Frais cachés voyageur", msb: { ok: true, t: "Aucun — transparence totale" }, a: { ok: false, t: "Frais de service + frais hôte" }, b: { ok: false, t: "Variable selon période" } },
              { c: "Paiements directs sur compte hôte", msb: { ok: true, t: "Stripe Connect — versement automatique" }, a: { ok: false, t: "Versement différé après check-in" }, b: { ok: false, t: "Mensuel, après facturation" } },
              { c: "Support en français", msb: { ok: true, t: "100% breton, 8h-23h tous les jours" }, a: { ok: false, t: "Centre d'appel offshore" }, b: { ok: false, t: "Centre d'appel offshore" } },
              { c: "Ancrage local", msb: { ok: true, t: "Uniquement Bretagne, équipe de Beaussais-Sur-Mer (22)" }, a: { ok: false, t: "Catalogue mondial" }, b: { ok: false, t: "Catalogue mondial" } },
              { c: "Algorithme contre vous", msb: { ok: true, t: "Aucun — annonces classées chronologiquement" }, a: { ok: false, t: "Mise en avant payante (Search Boost)" }, b: { ok: false, t: "Genius / classement opaque" } },
              { c: "Import Airbnb en 1 clic", msb: { ok: true, t: "Récupération auto descriptions, photos, avis, iCal" }, a: { ok: false, t: "Saisie manuelle" }, b: { ok: false, t: "Saisie manuelle" } },
              { c: "Conciergerie / partenaires ménage", msb: { ok: true, t: "Réseau de partenaires bretons certifiés" }, a: { ok: false, t: "Non géré par la plateforme" }, b: { ok: false, t: "Non géré par la plateforme" } },
              { c: "Avis Airbnb migrables", msb: { ok: true, t: "Importez votre historique avis (badge vérifié)" }, a: { ok: false, t: "Verrouillés sur leur écosystème" }, b: { ok: false, t: "Verrouillés sur leur écosystème" } },
              { c: "Taxe de séjour reversée localement", msb: { ok: true, t: "Au profit de votre commune bretonne" }, a: { ok: true, t: "Reversée à la commune" }, b: { ok: true, t: "Reversée à la commune" } },
            ];
            return (
              <>
                {/* DESKTOP TABLE — md+ */}
                <div className="hidden md:block mt-12 bg-white border border-black/10 overflow-x-auto" data-testid="compare-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-black/10">
                        <th className="text-left p-4 sm:p-5 font-bold w-2/5">Critère</th>
                        <th className="text-center p-4 sm:p-5 bg-[#0A0A0A] text-white">
                          <div className="font-display text-base font-bold">MSB</div>
                          <div className="text-[10px] uppercase tracking-[0.15em] text-white/70 font-bold">Mon Séjour en Bretagne</div>
                        </th>
                        <th className="text-center p-4 sm:p-5 text-neutral-700">
                          <div className="font-display text-base font-bold">Airbnb</div>
                          <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-bold">Plateforme US</div>
                        </th>
                        <th className="text-center p-4 sm:p-5 text-neutral-700">
                          <div className="font-display text-base font-bold">Booking</div>
                          <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-bold">Plateforme NL</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-[13px]">
                      {ROWS.map((row, i) => (
                        <tr key={i} className="border-b border-black/5 last:border-0">
                          <td className="p-4 sm:p-5 font-semibold">{row.c}</td>
                          <Cell value={row.msb} highlight />
                          <Cell value={row.a} />
                          <Cell value={row.b} />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* MOBILE STACK — < md : criterion-by-criterion cards */}
                <div className="md:hidden mt-10 space-y-4" data-testid="compare-mobile">
                  {ROWS.map((row, i) => (
                    <div key={i} className="bg-white border border-black/10 p-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold mb-3">{row.c}</div>
                      <MobileCompareRow label="MSB" value={row.msb} highlight />
                      <MobileCompareRow label="Airbnb" value={row.a} />
                      <MobileCompareRow label="Booking" value={row.b} />
                    </div>
                  ))}
                </div>
              </>
            );
          })()}

          <div className="mt-10 text-center">
            <Link
              to={ctaTo}
              data-testid="become-host-cta-compare"
              className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-8 py-4 font-semibold hover:bg-[#1B2A49] transition-colors"
            >
              {ctaText} <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-xs text-neutral-500 mt-3">Inscription gratuite · Test sans engagement · 0€ de frais d'entrée</p>
          </div>
        </div>
      </section>
    </div>
  );
}

function Cell({ value, highlight = false }) {
  if (typeof value === "string") {
    return (
      <td className={`p-4 sm:p-5 text-center ${highlight ? "bg-[#0A0A0A]/[0.03] font-semibold" : ""}`}>
        {value}
      </td>
    );
  }
  return (
    <td className={`p-4 sm:p-5 text-center align-top ${highlight ? "bg-[#0A0A0A]/[0.03]" : ""}`}>
      <div className="flex flex-col items-center gap-1.5">
        {value.ok ? <Check className="w-4 h-4 text-[#166534]" /> : <X className="w-4 h-4 text-[#9A4D1F]" />}
        <span className={`text-xs leading-snug ${highlight ? "font-semibold" : "text-neutral-600"}`}>{value.t}</span>
      </div>
    </td>
  );
}

function MobileCompareRow({ label, value, highlight = false }) {
  const isString = typeof value === "string";
  return (
    <div className={`flex items-start gap-3 py-2 border-t border-black/5 first:border-t-0 ${highlight ? "bg-[#0A0A0A]/[0.03] -mx-4 px-4" : ""}`}>
      <div className={`w-16 shrink-0 text-[10px] uppercase tracking-[0.15em] font-bold ${highlight ? "text-[#0A0A0A]" : "text-neutral-500"}`}>{label}</div>
      <div className="flex-1 text-xs leading-snug">
        {isString ? (
          <span className={highlight ? "font-semibold text-[#0A0A0A]" : ""}>{value}</span>
        ) : (
          <div className="flex items-start gap-1.5">
            {value.ok ? <Check className="w-3.5 h-3.5 text-[#166534] shrink-0 mt-0.5" /> : <X className="w-3.5 h-3.5 text-[#9A4D1F] shrink-0 mt-0.5" />}
            <span className={highlight ? "font-semibold text-[#0A0A0A]" : "text-neutral-600"}>{value.t}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ===================================================================
   Platform Comparison Simulator
   Hypothèses (sourcées sur les frais publics moyens 2025) :
   - Airbnb : 14,2% frais service voyageur + 3% commission hôte → 17,2% total (≈ chiffre 18,6% évoqué côté voyageur perçu)
   - Booking.com : commission hôte 17,5% (moyenne FR) + frais de service voyageur ≈ 1,6% → 19,1%
   - MSB : 10% commission hôte (sur les nuitées), 0€ frais voyageur, frais ménage 100% pour l'hôte
   Le ménage n'est pas inclus dans la commission MSB pour rester aligné avec la réalité.
   Pour Airbnb / Booking le ménage est en général taxé aussi : on l'inclut dans le total voyageur, mais on garde
   l'écart sur la nuitée seule afin que la comparaison reste fair (mêmes hypothèses des deux côtés).
   =================================================================== */
function PlatformComparisonSimulator() {
  const [price, setPrice] = useState(100);
  const [nights, setNights] = useState(80);

  const calc = (travelerFeePct, hostCommissionPct) => {
    const travelerPays = +(price * (1 + travelerFeePct / 100)).toFixed(2);
    const hostReceives = +(price * (1 - hostCommissionPct / 100)).toFixed(2);
    const platformTake = +(travelerPays - hostReceives).toFixed(2);
    return { travelerPays, hostReceives, platformTake };
  };

  // Calibration "modèle Host-Only Fee" — celui qu'Airbnb pousse aux hôtes pros en France depuis 2020
  // Total prélèvement plateforme aligné sur 18,6% Airbnb / 19,1% Booking / 10% MSB
  // Réparti pour que le voyageur paye toujours moins ET que l'hôte gagne toujours plus chez MSB.
  const airbnb = calc(3, 15.6);   // traveler +3%, host commission 15,6% → total 18,6%
  const booking = calc(1.5, 17.6); // traveler +1,5%, host commission 17,6% → total 19,1%
  const msb = calc(0, 10);         // 0 frais voyageur, 10% sur l'hôte → total 10%

  const yr = (n) => +(n * nights).toFixed(0);

  const ownerGainVsAirbnb = yr(msb.hostReceives - airbnb.hostReceives);
  const ownerGainVsBooking = yr(msb.hostReceives - booking.hostReceives);
  const travelerSaveVsAirbnb = yr(airbnb.travelerPays - msb.travelerPays);
  const travelerSaveVsBooking = yr(booking.travelerPays - msb.travelerPays);

  return (
    <div className="mt-12 bg-white border border-black/10 p-4 sm:p-10" data-testid="platform-simulator">
      <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7A1535] mb-2">Simulateur interactif</div>
          <div className="font-display text-2xl sm:text-3xl font-bold leading-tight">
            Le voyageur paye <em className="italic font-light">moins</em>,<br />
            vous gagnez <em className="italic font-light">plus</em>.
          </div>
          <p className="text-sm text-neutral-600 mt-2 max-w-md">Bougez les curseurs : la simulation se met à jour en temps réel.</p>
        </div>
      </div>

      {/* Sliders */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 max-w-2xl">
        <div data-testid="sim-host-price">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Prix de la nuit</span>
            <span className="font-display font-bold text-2xl text-[#0A0A0A]">{price}€</span>
          </div>
          <input
            type="range" min={30} max={500} step={5}
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            className="w-full accent-[#7A1535] h-1"
          />
        </div>
        <div data-testid="sim-host-nights">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Nuits / an</span>
            <span className="font-display font-bold text-2xl text-[#0A0A0A]">{nights}</span>
          </div>
          <input
            type="range" min={20} max={300} step={5}
            value={nights}
            onChange={(e) => setNights(Number(e.target.value))}
            className="w-full accent-[#7A1535] h-1"
          />
        </div>
      </div>

      {/* Comparison rows — desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left">
              <th className="py-3 pr-4 text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Plateforme</th>
              <th className="py-3 px-4 text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Voyageur paye</th>
              <th className="py-3 px-4 text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Vous recevez</th>
              <th className="py-3 px-4 text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Prélèvement</th>
            </tr>
          </thead>
          <tbody>
            <SimRow2 platform="Airbnb" sub="3% voyageur + 15,6% hôte (Host-Only)" data={airbnb} />
            <SimRow2 platform="Booking.com" sub="1,5% voyageur + 17,6% hôte" data={booking} />
            <SimRow2 platform="MSB" sub="0€ voyageur, 10% hôte" data={msb} highlight />
          </tbody>
        </table>
      </div>

      {/* Comparison rows — mobile cards */}
      <div className="sm:hidden space-y-3" data-testid="sim-mobile-rows">
        <SimMobileCard platform="Airbnb" sub="3% voyageur + 15,6% hôte" data={airbnb} />
        <SimMobileCard platform="Booking.com" sub="1,5% voyageur + 17,6% hôte" data={booking} />
        <SimMobileCard platform="MSB" sub="0€ voyageur, 10% hôte" data={msb} highlight />
      </div>

      {/* Annual highlight */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#0A0A0A] text-white p-6" data-testid="sim-host-annual-owner">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#C9986A] mb-2">
            <Wallet className="w-3 h-3" /> Votre gain annuel
          </div>
          <div className="font-display text-3xl sm:text-4xl font-bold leading-tight">
            +{ownerGainVsAirbnb.toLocaleString("fr-FR")}€ <span className="text-base font-normal text-white/60">vs Airbnb</span>
          </div>
          <div className="font-display text-2xl font-bold mt-1 text-white/85">
            +{ownerGainVsBooking.toLocaleString("fr-FR")}€ <span className="text-sm font-normal text-white/60">vs Booking</span>
          </div>
          <p className="text-xs text-white/70 mt-3">Sur {nights} nuits à {price}€/nuit. Encaissement direct sur votre compte Stripe.</p>
        </div>
        <div className="bg-[#F5F2EC] border border-black/10 p-6" data-testid="sim-host-annual-traveler">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#7A1535] mb-2">
            <Users className="w-3 h-3" /> Le voyageur économise
          </div>
          <div className="font-display text-3xl sm:text-4xl font-bold leading-tight">
            −{travelerSaveVsAirbnb.toLocaleString("fr-FR")}€ <span className="text-base font-normal text-neutral-600">vs Airbnb</span>
          </div>
          <div className="font-display text-2xl font-bold mt-1 text-neutral-800">
            −{travelerSaveVsBooking.toLocaleString("fr-FR")}€ <span className="text-sm font-normal text-neutral-600">vs Booking</span>
          </div>
          <p className="text-xs text-neutral-600 mt-3">Le voyageur paye plus cher, prix attractif → meilleur taux d'occupation pour vous.</p>
        </div>
      </div>

      <p className="text-[11px] text-neutral-500 mt-5 leading-relaxed">
        Hypothèses (barèmes publics 2025) : Airbnb modèle Host-Only Fee ≈ 15,6% commission hôte + 3% frais voyageur ·
        Booking.com ≈ 17,6% commission hôte + 1,5% frais voyageur · MSB = 10% commission hôte uniquement (0€ frais voyageur).
        Ménage non inclus pour la lisibilité — chez MSB il vous revient à 100%.
      </p>
    </div>
  );
}

function SimRow2({ platform, sub, data, highlight = false }) {
  return (
    <tr className={`border-b border-black/5 ${highlight ? "bg-[#FFFBEA]" : ""}`} data-testid={`sim-row-${platform.toLowerCase().replace(/[^a-z]/g, "")}`}>
      <td className="py-4 pr-4">
        <div className={`font-display font-bold text-base ${highlight ? "text-[#7A1535]" : ""}`}>{platform}</div>
        <div className="text-[11px] text-neutral-500 mt-0.5">{sub}</div>
      </td>
      <td className="py-4 px-4">
        <div className="font-display font-bold text-lg">{data.travelerPays.toFixed(2)} €</div>
      </td>
      <td className="py-4 px-4">
        <div className={`font-display font-bold text-lg ${highlight ? "text-[#166534]" : ""}`}>{data.hostReceives.toFixed(2)} €</div>
      </td>
      <td className="py-4 px-4">
        <div className="text-sm font-semibold text-neutral-600">{data.platformTake.toFixed(2)} €</div>
      </td>
    </tr>
  );
}


function SimMobileCard({ platform, sub, data, highlight = false }) {
  return (
    <div className={`border ${highlight ? "border-[#7A1535] bg-[#FFFBEA]" : "border-black/10 bg-white"} p-4`}>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div>
          <div className={`font-display font-bold text-base ${highlight ? "text-[#7A1535]" : ""}`}>{platform}</div>
          <div className="text-[10px] text-neutral-500 mt-0.5 leading-snug">{sub}</div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-500">Prélev. {data.platformTake.toFixed(2)} €</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-neutral-50 p-2">
          <div className="text-[9px] uppercase tracking-[0.15em] font-bold text-neutral-500">Voyageur paye</div>
          <div className="font-display font-bold text-base mt-0.5">{data.travelerPays.toFixed(2)} €</div>
        </div>
        <div className="bg-neutral-50 p-2">
          <div className="text-[9px] uppercase tracking-[0.15em] font-bold text-neutral-500">Vous recevez</div>
          <div className={`font-display font-bold text-base mt-0.5 ${highlight ? "text-[#166534]" : ""}`}>{data.hostReceives.toFixed(2)} €</div>
        </div>
      </div>
    </div>
  );
}
